import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * The current screen's title, so the top bar and the page body can stop
 * printing it twice.
 *
 * Ian, 2026-10-20: "get rid of the redundant double menus underneath". On a
 * phone the bar said "Home" from the nav table and the page said "My
 * Schedule" directly beneath it — two headings, in different words, for one
 * screen. On a desktop it was three, because the sidebar names the section
 * as well.
 *
 * `PageHeader` publishes its title here; the bar reads it. Exactly one of
 * them is visible at any width (see the `lg:` gates on each), so the title
 * is never written twice and the two can never disagree.
 */
const PageTitleContext = createContext<{
  title: string | null
  setTitle: (title: string | null) => void
}>({ title: null, setTitle: () => {} })

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)
  return (
    <PageTitleContext.Provider value={{ title, setTitle }}>{children}</PageTitleContext.Provider>
  )
}

/** Read by the header. */
export function usePageTitle(): string | null {
  return useContext(PageTitleContext).title
}

/**
 * Written by PageHeader.
 *
 * Cleared on unmount so a screen WITHOUT a PageHeader — the 404, a print
 * view — does not inherit the last one's title and claim to be it.
 */
export function usePublishPageTitle(title: string): void {
  const { setTitle } = useContext(PageTitleContext)
  useEffect(() => {
    setTitle(title)
    return () => setTitle(null)
  }, [setTitle, title])
}
